import { useCallback, useMemo, useRef, useState } from 'react';
import useDataGrid from '@components/_hooks/useDataGrid';
import useDataGridStyles from '@components/_hooks/useDataGridStyles';
import dynamic from 'next/dynamic';
import useDataGridVisibility from '@components/_hooks/useDataGridVisibility';
import { gridClasses } from '@mui/x-data-grid-pro';
import { alpha } from '@mui/system';
import { useTheme } from '@emotion/react';
import usePreferences from '@components/_hooks/usePreferences';
import useWindowSize from '@components/_hooks/useWindowSize';
import NoRows from 'modules/DataTables/components/NoRows';
const DataGridPro = dynamic(() =>
    import('@mui/x-data-grid-pro').then(mod => mod.DataGridPro), { ssr: false }
);
const CustomToolBar = dynamic(() => import('modules/DataTables/components/CustomToolBar'), { ssr: false });
const SizeSliders = dynamic(() => import('./SizeSliders'), { ssr: false });

const CompactDataGrid = (props) => {
    const { quoteTable } = usePreferences();
    const quoteFontFamily = quoteTable.quoteFontFamily();

    const {
        SlotProps = true,
        ToolbarProps = true,
        ShowPagination = true,
        CellClick = false,
        ShowFilters = false,
        CheckBoxSelection = false,
        visibleColumns = {},
        updateVisiblePreferences = false,
        preferenceName = null,
        sx = {},
        defaultPagination = 25,
        checkedRows,
        alternativeRows = false,
        maxHeight = 1000,
        dataSource = null,
        lazyLoading = false,
        slots: restSlots,
        slotProps: restSlotProps,
        density: initialDensity = 'compact',
        ...rest
    } = props;

    const [density, setDensity] = useState(initialDensity);

    const isDataSourceMode = !!dataSource;
    const rowCount = props?.rows?.length || 0;
    const pageSizeOptions = props?.pageSizeOptions || [25, 50, 100, 150, 200, rowCount];

    const { isMobile } = useWindowSize();
    const dataGridSliders = useDataGridStyles();
    const fontSize = ShowFilters ? dataGridSliders.fontSize : 12;
    const rowHeight = ShowFilters ? dataGridSliders.rowHeight : 35;

    const { Toolbar, pagination, singleCellModel } = useDataGrid(defaultPagination);
    const visibility = useDataGridVisibility(visibleColumns, updateVisiblePreferences, preferenceName);

    const displayPageSizeOptions = pageSizeOptions
        .map((option) => option === 'all' ? rowCount : option)
        .filter((option) => Number.isFinite(option) && option > 0);

    const theme = useTheme();
    const wrapperRef = useRef(null);

    const handleWheel = useCallback((event) => {
        const scroller = wrapperRef.current?.querySelector('.MuiDataGrid-virtualScroller');
        if (!scroller) return;

        scroller.scrollTop += event.deltaY * 1.5;
    }, []);

    const isCheckboxSelection = Boolean(CheckBoxSelection || props?.checkboxSelection);

    const normalisedRowSelectionModel = useMemo(() => {
        const selectedRows = checkedRows?.selectedRows;

        if (!selectedRows) return undefined;

        if (Array.isArray(selectedRows)) {
            return {
                type: 'include',
                ids: new Set(selectedRows),
            };
        }

        if (selectedRows instanceof Set) {
            return {
                type: 'include',
                ids: selectedRows,
            };
        }

        if (
            typeof selectedRows === 'object' &&
            selectedRows.type &&
            selectedRows.ids instanceof Set
        ) {
            return selectedRows;
        }

        if (
            typeof selectedRows === 'object' &&
            selectedRows.type &&
            Array.isArray(selectedRows.ids)
        ) {
            return {
                type: selectedRows.type,
                ids: new Set(selectedRows.ids),
            };
        }

        return undefined;
    }, [checkedRows?.selectedRows]);


    const conditionalProps = useMemo(() => {
        return {
            ...(SlotProps && {
                slots: {
                    toolbar: CustomToolBar,
                    noRowsOverlay: NoRows,
                },
                slotProps: {
                    toolbar: {
                        density,
                        onDensityChange: setDensity,
                    },
                },
            }),
            ...(ToolbarProps && {
                ...Toolbar.disableNotAgentOrMobile(),
            }),
            ...(!isDataSourceMode && ShowPagination && {
                pagination: true,
                paginationModel: pagination.paginationModel,
                onPaginationModelChange: pagination.handlePagination,
                pageSizeOptions: displayPageSizeOptions,
            }),
            ...(CellClick && {
                cellModesModel: singleCellModel.cellModesModel,
                onCellModesModelChange: singleCellModel.handleCellModesModelChange,
                onCellClick: singleCellModel.handleCellClick,
            }),
            ...(isCheckboxSelection && {
                checkboxSelection: true,
            }),
            ...(isCheckboxSelection && checkedRows?.handleSelectionChange && {
                onRowSelectionModelChange: checkedRows.handleSelectionChange,
            }),
            ...(isCheckboxSelection && normalisedRowSelectionModel && {
                rowSelectionModel: normalisedRowSelectionModel,
            }),
        };
    }, [
        ToolbarProps,
        ShowPagination,
        CellClick,
        isCheckboxSelection,
        Toolbar,
        pagination.paginationModel,
        pagination.handlePagination,
        displayPageSizeOptions,
        singleCellModel.cellModesModel,
        singleCellModel.handleCellModesModelChange,
        singleCellModel.handleCellClick,
        checkedRows,
        normalisedRowSelectionModel,
        isDataSourceMode,
    ]);

    return (
        <>
            {ShowFilters && (
                <SizeSliders {...dataGridSliders} />
            )}

            <div
                ref={wrapperRef}
                onWheel={handleWheel}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: maxHeight,
                    width: '100%',
                }}
            >
                <DataGridPro
                    showToolbar={SlotProps}
                    density={density}
                    rowHeight={Number(rowHeight)}
                    rowBufferPx={400}
                    columnBufferPx={200}
                    lazyLoading={lazyLoading}
                    dataSource={dataSource}
                    getRowClassName={(params) =>
                        alternativeRows && params.indexRelativeToCurrentPage % 2 !== 0 ? 'odd' : 'even'
                    }
                    sx={{
                        [`& .${gridClasses.row}.odd`]: {
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                            },
                        },
                        '& .MuiTypography-root': {
                            fontSize: `${fontSize}px`,
                        },
                        '& .MuiChip-label': {
                            fontSize: `${fontSize}px`,
                        },
                        '& .MuiDataGrid-cell': {
                            fontSize: `${fontSize}px`,
                            '--row-height': `${rowHeight}px`,
                            '--font-size': `${fontSize}px`,
                            justifyContent: 'start',
                            ...(quoteFontFamily == 'roboto' && {
                                fontFamily: 'var(--font-roboto)',
                            }),
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            fontSize: `${fontSize}px`,
                        },
                        '& .MuiBox-root': {
                            fontSize: `${fontSize}px`,
                        },
                        input: {
                            fontSize: `${fontSize}px`,
                        },
                        '& .MuiDataGrid-cell--withRenderer': {
                            justifyContent: 'start',
                        },
                        '.child-row': {
                            backgroundColor: '#f0f0f0',
                        },
                        [`& .${gridClasses.columnHeaderFilterInput} .MuiSelect-select`]: {
                            paddingTop: 'calc(var(--DataGrid-t-spacing-unit) * 0.25)',
                            paddingBottom: 'calc(var(--DataGrid-t-spacing-unit) * 0.25)',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        ...sx,
                    }}
                    {...conditionalProps}
                    {...visibility}
                    {...rest}
                />
            </div>
        </>
    );
};

export default CompactDataGrid;